import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  getApifacilAccountProfile,
  removeApifacilAccountPhoto,
  updateApifacilAccountProfile,
} from "../../api/client";
import { whatsappQrCapabilities } from "../../utils/whatsappQrCapabilities";

const DISAPPEARING_OPTIONS = [
  { value: "0", label: "Desligado" },
  { value: "86400", label: "24 horas" },
  { value: "604800", label: "7 dias" },
  { value: "7776000", label: "90 dias" },
];

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || "");
      const comma = raw.indexOf(",");
      resolve(comma >= 0 ? raw.slice(comma + 1) : raw);
    };
    reader.onerror = () => reject(reader.error || new Error("Falha ao ler o arquivo."));
    reader.readAsDataURL(file);
  });
}

function photoSrcFromProfile(profile) {
  if (profile?.photoUrl) return profile.photoUrl;
  if (profile?.photoBase64) {
    const raw = String(profile.photoBase64);
    return raw.startsWith("data:") ? raw : `data:image/jpeg;base64,${raw}`;
  }
  return "";
}

export default function WhatsappQrProfileSection({ channelId, version }) {
  const caps = whatsappQrCapabilities(version);
  const [name, setName] = useState("");
  const [about, setAbout] = useState("");
  const [photoBase64, setPhotoBase64] = useState("");
  const [photoName, setPhotoName] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const [disappearing, setDisappearing] = useState("0");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const applyProfile = useCallback((profile) => {
    setName(String(profile?.name || ""));
    setAbout(String(profile?.about || ""));
    const seconds =
      profile?.disappearingSeconds != null && Number.isFinite(Number(profile.disappearingSeconds))
        ? String(Number(profile.disappearingSeconds))
        : "0";
    setDisappearing(DISAPPEARING_OPTIONS.some((opt) => opt.value === seconds) ? seconds : seconds);
    setPhotoPreview(photoSrcFromProfile(profile));
    setPhotoBase64("");
    setPhotoName("");
  }, []);

  const loadProfile = useCallback(async () => {
    if (!channelId) return;
    setLoading(true);
    setMsg(null);
    try {
      const data = await getApifacilAccountProfile(channelId);
      applyProfile(data || {});
    } catch (err) {
      setMsg({
        severity: "warning",
        text: err?.message || "Não foi possível carregar o perfil atual do WhatsApp.",
      });
    } finally {
      setLoading(false);
    }
  }, [applyProfile, channelId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  if (!caps.accountProfile || !channelId) return null;

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await updateApifacilAccountProfile(channelId, {
        ...(name ? { name } : {}),
        ...(about ? { about } : {}),
        ...(photoBase64 ? { photoBase64 } : {}),
        disappearingSeconds: Number(disappearing) || 0,
      });
      setMsg({ severity: "success", text: "Perfil da conta atualizado." });
      await loadProfile();
    } catch (err) {
      setMsg({ severity: "error", text: err?.message || "Não foi possível atualizar o perfil." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Perfil da conta conectada
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        Os campos abaixo mostram o que já está no WhatsApp. Altere só o que quiser atualizar.
      </Typography>
      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Carregando perfil atual…
          </Typography>
        </Stack>
      ) : null}
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar src={photoPreview || undefined} alt={name || "Foto do perfil"} sx={{ width: 56, height: 56 }} />
          <Button component="label" variant="outlined" disabled={busy || loading} sx={{ flex: 1 }}>
            {photoName || (photoPreview ? "Trocar foto" : "Escolher foto")}
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const b64 = await readFileAsBase64(file);
                  setPhotoBase64(b64);
                  setPhotoName(file.name);
                  setPhotoPreview(`data:${file.type || "image/jpeg"};base64,${b64}`);
                } catch (err) {
                  setMsg({ severity: "error", text: err?.message || "Falha ao ler a foto." });
                }
              }}
            />
          </Button>
        </Stack>
        <TextField
          size="small"
          label="Nome de exibição"
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 25 }}
          disabled={loading}
        />
        <TextField
          size="small"
          label="Recado"
          value={about}
          onChange={(e) => setAbout(e.target.value)}
          inputProps={{ maxLength: 139 }}
          disabled={loading}
        />
        <TextField
          select
          size="small"
          label="Mensagens temporárias"
          value={
            DISAPPEARING_OPTIONS.some((opt) => opt.value === String(disappearing))
              ? String(disappearing)
              : "0"
          }
          onChange={(e) => setDisappearing(e.target.value)}
          disabled={loading}
        >
          {DISAPPEARING_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" disabled={busy || loading} onClick={save}>
            Salvar perfil
          </Button>
          <Button
            color="warning"
            disabled={busy || loading}
            onClick={async () => {
              setBusy(true);
              try {
                await removeApifacilAccountPhoto(channelId);
                setPhotoBase64("");
                setPhotoName("");
                setPhotoPreview("");
                setMsg({ severity: "success", text: "Foto removida." });
              } catch (err) {
                setMsg({ severity: "error", text: err?.message || "Falha ao remover a foto." });
              } finally {
                setBusy(false);
              }
            }}
          >
            Remover foto
          </Button>
        </Stack>
        {msg ? <Alert severity={msg.severity}>{msg.text}</Alert> : null}
      </Stack>
    </Box>
  );
}
